import { colorStrings, mediaQueriesBP } from "@hardware/Utilities/variables";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setFieldValue from "@insite/client-framework/Store/Components/ContactUsForm/Handlers/SetFieldValue";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ContactFormContext } from "@insite/content-library/Widgets/ContactUs/ContactForm";
import Checkbox from "@insite/mobius/Checkbox";
import CheckboxGroup from "@insite/mobius/CheckboxGroup";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, ThemeProps, withTheme } from "styled-components";

const enum fields {
    title = "title",
    checkboxLabel1 = "checkboxLabel1",
    checkboxLabel2 = "checkboxLabel2",
    checkboxLabel3 = "checkboxLabel3",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.checkboxLabel1]: string;
        [fields.checkboxLabel2]: string;
        [fields.checkboxLabel3]: string;
    };
}

const mapStateToProps = (state: ApplicationState) => ({
    electronicComponents: state.components.contactUsForm.fieldValues["electronicComponents"],
    electronicsHardware: state.components.contactUsForm.fieldValues["electronicsHardware"],
    fasteners: state.components.contactUsForm.fieldValues["fasteners"],
});

const mapDispatchToProps = {
    setFieldValue,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface CatalogLinesStyles {
    catalogLinesTextField?: TextFieldPresentationProps;
    title?: TypographyPresentationProps;
    checkboxwrapper?: InjectableCss;
    checkboxstyle?: InjectableCss;
    checkbox2style?: InjectableCss;
}

export const catalogLinesStyles: CatalogLinesStyles = {
    title: {
        variant: "p",
        css: css`
            font-size: 15px;
            font-weight: 600;
        `,
    },
    checkboxwrapper: {
        css: css`
            display: flex;
            justify-content: flex-start;
            margin: 0 -15px 0;
            flex-wrap: wrap;
            padding: 0 15px;
            flex-direction: column;
            align-items: baseline;

            ${mediaQueriesBP.maxMD} {
                flex-direction: column;
                align-items: baseline;
            }

            & legend {
                margin-bottom: 0 !important;
             }
        `,
    },
    checkboxstyle: {
        css: css`
            margin-top: 0 !important;
            width: calc(33.33%);
            ${mediaQueriesBP.maxMD} {
                width: 100%;
                padding-bottom: 5px;
            }
        `,
    },
};

const styles = catalogLinesStyles;

const CatalogLines: React.FC<Props> = ({ fields, setFieldValue }) => {
    const { title, checkboxLabel1, checkboxLabel2, checkboxLabel3 } = fields;
    const [isElectronicComponents, setElectronicComponents] = useState(false);
    const [isElectronicHardware, setElectronicHardware] = useState(false);
    const [isFasteners, setFasteners] = useState(false);
    const brands: string[] = [];
    
    useEffect(() => {  
        if (isElectronicComponents) {
            brands.push("Electronic Components");
        } else {
            const index = brands.indexOf("Electronic Components");
            if(index > -1){brands.splice(index, 1);}            
        }

        if (isElectronicHardware) {
            brands.push("Electronics Hardware");
        } else {
            const index = brands.indexOf("Electronics Hardware");
            if(index > -1){brands.splice(index, 1);}
        }

        if (isFasteners) {
            brands.push("Fasteners");
        } else {
            const index = brands.indexOf("Fasteners");
            if(index > -1){brands.splice(index, 1);}
        }

        setFieldValue({ key: "brands", value: brands.toString() });
    }, [isElectronicComponents,isElectronicHardware,isFasteners]);

    return (
        <StyledWrapper>
            <Typography {...styles.title}>{title}</Typography>
            <CheckboxGroup {...styles.checkboxwrapper}>
                <Checkbox
                    {...styles.checkboxstyle}
                    onChange={(e, value) => setElectronicComponents(value)}
                    checked={isElectronicComponents}
                >
                    {checkboxLabel1}
                </Checkbox>
                <Checkbox
                    {...styles.checkboxstyle}
                    onChange={(e, value) => setElectronicHardware(value)}
                    checked={isElectronicHardware}
                >
                    {checkboxLabel2}
                </Checkbox>
                <Checkbox {...styles.checkboxstyle} onChange={(e, value) => setFasteners(value)} checked={isFasteners}>
                    {checkboxLabel3}
                </Checkbox>
            </CheckboxGroup>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(CatalogLines),
    definition: {
        group: "Contact Us",
        icon: "input-pipe",
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Please send me catalogs for the following lines:",
                isRequired: true,
            },
            {
                name: fields.checkboxLabel1,
                displayName: "Checkbox Label 1",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Electronic Components",
                isRequired: true,
            },
            {
                name: fields.checkboxLabel2,
                displayName: "Checkbox Label 2",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Electronic Hardware",
                isRequired: true,
            },
            {
                name: fields.checkboxLabel3,
                displayName: "Checkbox Label 3",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Fasteners",
                isRequired: true,
            },
        ],
    },
};

export default widgetModule;
